import React, { useState } from 'react';
import { Switch, Tooltip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import {
	useDisableDebtorAutomationMutation,
	useEnableDebtorAutomationMutation,
} from '@/features/deals/debtorAutomationApi';

/**
 * Switch to enable/disable debtor automated reminders from list or kanban views.
 */
export const DebtorAutomationToggle = ({
	dealId,
	automationEnabled = false,
	disabled = false,
}) => {
	const dispatch = useDispatch();
	const [enableAutomation, enableMutation] = useEnableDebtorAutomationMutation();
	const [disableAutomation, disableMutation] = useDisableDebtorAutomationMutation();
	const [coolingDown, setCoolingDown] = useState(false);

	const checked = Boolean(automationEnabled);
	const isBusy = enableMutation.isLoading || disableMutation.isLoading || coolingDown;

	const handleChange = async (event) => {
		event.stopPropagation();
		if (!dealId || disabled || isBusy) {
			return;
		}

		const nextEnabled = event.target.checked;
		setCoolingDown(true);

		try {
			if (nextEnabled) {
				await enableAutomation({ dealId }).unwrap();
				dispatch(
					showToast({
						message: 'Automated reminders enabled.',
						success: true,
						autohide: true,
					}),
				);
			} else {
				await disableAutomation({ dealId }).unwrap();
				dispatch(
					showToast({
						message: 'Automated reminders disabled.',
						success: true,
						autohide: true,
					}),
				);
			}
		} catch (error) {
			console.error('Error updating debtor automation', error);
			dispatch(
				showToast({
					message:
						error?.data?.error ??
						error?.data?.message ??
						error?.error ??
						error?.message ??
						`Unable to ${nextEnabled ? 'enable' : 'disable'} automated reminders.`,
					error: true,
				}),
			);
		} finally {
			setTimeout(() => setCoolingDown(false), 6000);
		}
	};

	const handleClick = (event) => {
		event.stopPropagation();
		if (event.target.type !== 'checkbox') {
			event.preventDefault();
		}
	};

	return (
		<Tooltip title={`Automated reminders ${checked ? 'on' : 'off'}`}>
			<span onClick={handleClick} onMouseDown={handleClick}>
				<Switch
					size='small'
					checked={checked}
					onChange={handleChange}
					disabled={disabled || !dealId || isBusy}
					slotProps={{ input: { 'aria-label': 'Toggle automated reminders' } }}
				/>
			</span>
		</Tooltip>
	);
};

export default DebtorAutomationToggle;
